// src/lib/indiaPayroll.js

// --- helpers ---
const round = (n) => Math.round((n + Number.EPSILON) * 100) / 100;

export const defaultConfig = {
  struct: { basicPct: 40, hraPctOfBasic: 50, includeGratuity: true }, // Basic% of CTC; HRA% of Basic
  pf: { enabled: true, applyPfWageCeiling: true, pfWageCeiling: 15000 }, // Employer may cap at 15k
  esi: { enabled: true, monthlyWageCeiling: 21000 }, // ESI eligibility threshold
  pt: { state: "NONE", manualMonthly: 0 }, // NONE | MH | KA | MANUAL
  tax: {
    regime: "NEW", // NEW | OLD
    stdDeduction: 50000, // default official for AY 2025-26 under NEW
    // NEW regime slabs (AY 2025-26) — you can override in UI if needed
    newSlabs: [
      { upto: 300000, rate: 0 },
      { upto: 700000, rate: 0.05 },
      { upto: 1000000, rate: 0.10 },
      { upto: 1200000, rate: 0.15 },
      { upto: 1500000, rate: 0.20 },
      { upto: Infinity, rate: 0.30 },
    ],
    oldSlabs: [
      { upto: 250000, rate: 0 },
      { upto: 500000, rate: 0.05 },
      { upto: 1000000, rate: 0.20 },
      { upto: Infinity, rate: 0.30 },
    ],
    rebate87A_new_threshold: 700000,
    rebate87A_old_threshold: 500000,
    cess: 0.04,
    extraDeductionsOld: 0,  // e.g., 80C/80D user-entered total
  },
};

// --- Professional Tax presets (simple) ---
export function calcPTMonthly(grossMonthly, state, manual = 0) {
  switch (state) {
    case "MH": {
      // Simplified: ≥ ₹10,000 → ₹200/mo (Feb ₹300 not modelled here)
      return grossMonthly >= 10000 ? 200 : (grossMonthly > 7500 ? 175 : 0);
    }
    case "KA": {
      // Simplified: ≥ ₹25,000 → ₹200/mo (Feb ₹300 not modelled here)
      return grossMonthly >= 25000 ? 200 : 0;
    }
    case "MANUAL":
      return manual || 0;
    default:
      return 0;
  }
}

// --- PF ---
export function calcPF({ basicMonthly, applyCeiling = true, ceiling = 15000 }) {
  const pfWage = applyCeiling ? Math.min(basicMonthly, ceiling) : basicMonthly;
  const employeePF = round(0.12 * pfWage);
  const employerPF = round(0.12 * pfWage);
  // EPS note: employer's 8.33% of up to ₹15,000 (max ₹1250) is pension; remainder to EPF.
  const eps = round(Math.min(pfWage, 15000) * 0.0833);
  const epfEmployer = round(employerPF - eps);
  return { pfWage, employeePF, employerPF, eps, epfEmployer };
}

// --- ESI ---
export function calcESI({ esiEligible, esiWage }) {
  if (!esiEligible) return { employeeESI: 0, employerESI: 0 };
  const employeeESI = round(0.0075 * esiWage);
  const employerESI = round(0.0325 * esiWage);
  return { employeeESI, employerESI };
}

// --- Tax (generic slab applier) ---
export function calcTaxFromSlabs({ taxable, slabs, rebateThreshold, cess }) {
  if (taxable <= rebateThreshold) return 0;
  let tax = 0, prev = 0;
  for (const s of slabs) {
    const slabTop = Math.min(s.upto, taxable);
    if (slabTop > prev) tax += (slabTop - prev) * s.rate;
    if (s.upto === Infinity || taxable <= s.upto) break;
    prev = s.upto;
  }
  tax = Math.max(0, tax);
  return round(tax * (1 + (cess || 0)));
}

export function calcAnnualTax({ annualIncome, cfg = defaultConfig.tax }) {
  const { regime, stdDeduction, newSlabs, oldSlabs, rebate87A_new_threshold, rebate87A_old_threshold, cess, extraDeductionsOld } = cfg;

  if (regime === "NEW") {
    const taxable = Math.max(0, annualIncome - stdDeduction);
    return calcTaxFromSlabs({
      taxable,
      slabs: newSlabs,
      rebateThreshold: rebate87A_new_threshold,
      cess,
    });
  } else {
    const taxable = Math.max(0, annualIncome - (stdDeduction || 0) - (extraDeductionsOld || 0));
    return calcTaxFromSlabs({
      taxable,
      slabs: oldSlabs,
      rebateThreshold: rebate87A_old_threshold,
      cess,
    });
  }
}

// --- CTC breakup + all oncosts ---
export function computeCtc({ annualCTC, cfg = defaultConfig }) {
  const monthlyCTC = annualCTC / 12;

  // structure
  const basicMonthly = round((cfg.struct.basicPct / 100) * monthlyCTC);
  const hraMonthly = round((cfg.struct.hraPctOfBasic / 100) * basicMonthly);
  // (optional) gratuity estimate 4.81% of BASIC (employer cost)
  const gratuityMonthly = cfg.struct.includeGratuity ? round(basicMonthly * 0.0481) : 0;

  // Special is the balancing figure (employer cost side before PF/ESI)
  const prelim = basicMonthly + hraMonthly;
  let specialMonthly = round(monthlyCTC - prelim - gratuityMonthly);

  // PF
  const { employeePF, employerPF, pfWage, eps, epfEmployer } = cfg.pf.enabled
    ? calcPF({ basicMonthly, applyCeiling: cfg.pf.applyPfWageCeiling, ceiling: cfg.pf.pfWageCeiling })
    : { employeePF: 0, employerPF: 0, pfWage: 0, eps: 0, epfEmployer: 0 };

  // ESI: use gross (cash) wages = basic + hra + special
  const cashGrossMonthly = basicMonthly + hraMonthly + specialMonthly;
  const esiEligible = cfg.esi.enabled && cashGrossMonthly <= cfg.esi.monthlyWageCeiling;
  const { employeeESI, employerESI } = calcESI({ esiEligible, esiWage: cashGrossMonthly });

  // Professional Tax
  const ptMonthly = calcPTMonthly(cashGrossMonthly, cfg.pt.state, cfg.pt.manualMonthly);

  // Employee take-home impact (pre-tax): gross - employee deductions
  const employeeGrossMonthly = cashGrossMonthly;
  const employeeDeductionsMonthly = employeePF + employeeESI + ptMonthly;
  const preTaxTakeHomeMonthly = round(employeeGrossMonthly - employeeDeductionsMonthly);

  // Income tax (annual) on gross salary (cash) — CTC extras (PF/Gratuity) are not taxed as income to employee
  const annualGrossCash = round(employeeGrossMonthly * 12);
  const annualTax = calcAnnualTax({ annualIncome: annualGrossCash, cfg: cfg.tax });
  const monthlyTDS = round(annualTax / 12);
  const takeHomeMonthly = round(preTaxTakeHomeMonthly - monthlyTDS);

  // Employer cost reconciliation (CTC side): PF & ESI & gratuity are already part of CTC?
  // Our model assumed CTC input already includes gratuity (if enabled) and employer PF/ESI inside the CTC envelope.
  // If you want them on top of CTC, set includeGratuity=false and adjust inputs accordingly.

  return {
    monthly: {
      basic: basicMonthly,
      hra: hraMonthly,
      special: specialMonthly,
      gratuity: gratuityMonthly,
      pf: { employee: employeePF, employer: employerPF, wage: pfWage, eps, epfEmployer },
      esi: { employee: employeeESI, employer: employerESI, eligible: esiEligible },
      pt: ptMonthly,
      grossCash: employeeGrossMonthly,
      preTaxTakeHome: preTaxTakeHomeMonthly,
      tds: monthlyTDS,
      takeHome: takeHomeMonthly,
    },
    annual: {
      grossCash: annualGrossCash,
      tax: annualTax,
    },
  };
}
